import { useState } from 'react';
import { Box, Flex, Heading, Tag, Button, Progress, Switch, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, useDisclosure, Text, Grid } from '@chakra-ui/react';
import { CopyIcon, InfoIcon } from '@chakra-ui/icons';
import { DiskInfo } from '../../types/stats';
import { parsePercentage, parseStorageGB, getAlertColor, getProgressColor } from '../../utils/stats';
import { FaDatabase } from 'react-icons/fa';
import { FiHardDrive } from 'react-icons/fi';

interface DisksCardProps {
  data: DiskInfo[];
  paused: boolean;
}

export function DisksCard({ data, paused }: DisksCardProps) {
  const [showHighUsageOnly, setShowHighUsageOnly] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDisk, setSelectedDisk] = useState<DiskInfo | null>(null);

  const filteredDisks = showHighUsageOnly 
    ? data.filter(disk => parsePercentage(disk.usage) > 80)
    : data;

  const getUsageStats = (disk: DiskInfo) => {
    const usage = parsePercentage(disk.usage);
    const sizeGB = parseStorageGB(disk.size);
    const usedGB = parseStorageGB(disk.used);
    const freeGB = sizeGB - usedGB;
    
    return { usage, sizeGB, usedGB, freeGB };
  };

  const handleShowInfo = (disk: DiskInfo) => {
    setSelectedDisk(disk);
    onOpen();
  }

  return (
    <Box borderWidth="1px" borderRadius="2xl" p={4} position="relative" overflow="hidden">
      {paused && (
        <Tag borderRadius={"2xl"} position="absolute" top={2} right={2} colorScheme="yellow">Paused</Tag>
      )}
      
      <Flex as="header" alignItems="center" justifyContent="space-between" pb={2}>
        <Heading as="h3" size="sm" display="flex" alignItems="center" gap={2}>
          {/* <CopyIcon /> */}
          <FiHardDrive />
          Disks ({filteredDisks.length})
        </Heading>
        <Flex alignItems="center" gap={2}>
          <Switch
            isChecked={showHighUsageOnly}
            onChange={(e) => setShowHighUsageOnly(e.target.checked)}
            size="sm"
          />
          <Text fontSize="xs" color="gray.500">&gt;80%</Text>
        </Flex>
      </Flex>
      
      <Box>
        {filteredDisks.length === 0 ? (
          <Text textAlign="center" py={4} color="gray.500">
            {showHighUsageOnly ? 'No disks over 80% usage' : 'No disks found'}
          </Text>
        ) : (
          filteredDisks.map((disk, index) => {
            const { usage, sizeGB, usedGB, freeGB } = getUsageStats(disk);
            const alertColor = getAlertColor(usage);
            const progressColor = getProgressColor(usage);

            return (
              <Box key={index} mb={4}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Flex alignItems="center" gap={2}>
                    <Text fontFamily="mono" fontSize="sm">{disk.mount}</Text>
                    <Tag borderRadius={"2xl"} colorScheme={alertColor === 'danger' ? 'red' : alertColor === 'warning' ? 'yellow' : 'green'} fontSize="xs">
                      {disk.usage}
                    </Tag>
                  </Flex>
                  
                  <Button variant="ghost" size="sm" onClick={() => handleShowInfo(disk)}>
                    <InfoIcon />
                  </Button>
                </Flex>
                
                <Progress
                  borderRadius={"xl"} 
                  value={usage} 
                  colorScheme={alertColor === 'danger' ? 'red' : alertColor === 'warning' ? 'yellow' : 'green'}
                  size="sm"
                  mt={2}
                />
                
                <Flex justifyContent="space-between" fontSize="xs" color="gray.500" mt={1}>
                  <Text>{disk.used} used</Text>
                  <Text>{freeGB.toFixed(1)} GB free</Text>
                </Flex>
              </Box>
            );
          })
        )}
      </Box>

      {selectedDisk && (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>Disk Details: {selectedDisk.mount}</DrawerHeader>

            <DrawerBody>
              <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={6}>
                <Box>
                  <Text fontSize="sm" color="gray.500">Mount Point</Text>
                  <Text fontFamily="mono">{selectedDisk.mount}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Usage</Text>
                  <Text fontFamily="mono">{selectedDisk.usage}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Total Size</Text>
                  <Text fontFamily="mono">{selectedDisk.size}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Used Space</Text>
                  <Text fontFamily="mono">{selectedDisk.used}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Free Space</Text>
                  <Text fontFamily="mono" color="green.500">{(parseStorageGB(selectedDisk.size) - parseStorageGB(selectedDisk.used)).toFixed(1)} GB</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">File System</Text>
                  <Text fontFamily="mono">ext4</Text>
                </Box>
              </Grid>
              
              <Box mt={6}>
                <Text fontSize="sm" color="gray.500">Usage Breakdown</Text>
                <Progress
                  borderRadius={"xl"} 
                  value={parsePercentage(selectedDisk.usage)} 
                  colorScheme={getAlertColor(parsePercentage(selectedDisk.usage)) === 'danger' ? 'red' : getAlertColor(parsePercentage(selectedDisk.usage)) === 'warning' ? 'yellow' : 'green'}
                  size="md"
                  mt={2}
                />
                <Flex justifyContent="space-between" fontSize="xs" color="gray.500" mt={1}>
                  <Text>0%</Text>
                  <Text>{Math.round(parsePercentage(selectedDisk.usage))}%</Text>
                  <Text>100%</Text>
                </Flex>
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </Box>
  );
}
